// Change detection.
//
// Compares current observations against stored history across multiple windows
// and only flags *meaningful* changes (above per-indicator thresholds). Each
// flagged change becomes a structured event answering the five product
// questions: what happened, why it matters, BCH relevance, what confirms it.

import { INDICATORS_BY_ID } from './indicators';
import { getHistoricalValue } from './store';
import type { Observation, ChangeEvent } from './types';

const WINDOWS: { key: ChangeEvent['window']; ms: number }[] = [
  { key: '24h', ms: 24 * 60 * 60 * 1000 },
  { key: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d', ms: 30 * 24 * 60 * 60 * 1000 },
  { key: '90d', ms: 90 * 24 * 60 * 60 * 1000 },
];

// Minimum % move (over the best window) to consider meaningful, per indicator.
const THRESHOLDS: Record<string, number> = {
  usdjpy: 1.5,
  dxy: 1.0,
  bch_btc: 4,
  bch_usd: 6,
  ust_10y: 4,
  ust_2y: 4,
  fima_repo: 20,
  tga: 8,
  reverse_repo: 10,
  stablecoin_supply: 3,
  total_mcap: 5,
  wti_crude: 5,
  brent_crude: 5,
  brent_wti_spread: 25,
  // NOTE: btc_trend, bch_momentum, bch_rel_strength, btc_dominance are handled
  // by POINT_MOVE_THRESHOLDS (they're percentage-unit indicators), not here.
};

const DEFAULT_THRESHOLD = 5;

// Sanity ceiling: the largest % move a level indicator can plausibly make in a
// single comparison window. Slow-moving macro balances (TGA, reverse repo, Fed
// balance sheet, etc.) physically cannot swing ~99% day-to-day. A move above
// the ceiling is almost always a data/unit discontinuity in stored history
// (e.g. one point in millions, the next in billions -> a fake "-99.9%"), not a
// real event, so we discard that comparison rather than headline garbage.
// Point-mode indicators are exempt (they're bounded percentages already).
const SANITY_CEILING_PCT: Record<string, number> = {
  tga: 60,
  reverse_repo: 70,
  fed_balance_sheet: 25,
  fed_fx_assets: 80,
  fima_repo: 200,
  japan_ust_holdings: 40,
  stablecoin_supply: 40,
  total_mcap: 60,
  bch_usd: 200,
  bch_btc: 200,
};
const DEFAULT_SANITY_CEILING = 300;

// Indicators that are too noisy to headline as "changes". Raw 24h trading
// volume routinely doubles or halves day-to-day on no meaningful signal (a
// single large trade, an exchange promo), so a ratio-based % change produces
// absurd "+200%" alerts. Volume is a weight-1 *confirming* input, not a signal
// on its own — exclude it from change-detection events entirely.
const EXCLUDED_FROM_CHANGES = new Set<string>(['bch_volume']);

/** True if an indicator's changes are measured in percentage points, not %. */
export function isPointMove(indicatorId: string): boolean {
  return indicatorId in POINT_MOVE_THRESHOLDS;
}

// Indicators whose UNIT is already a percentage. For these a ratio-based
// "percent change of a percent" is either meaningless or confusing:
//   - Zero-centered ones (btc_trend, bch_momentum, bch_rel_strength) explode
//     when the baseline is near zero (e.g. +175% for a 0.7-point wiggle).
//   - Level ones (btc_dominance) don't explode, but "dominance +3.6%" is
//     ambiguous vs. a 2 percentage-POINT move.
// So we compare the ABSOLUTE change in percentage points; the threshold here
// is in points and the change is worded in points, not "%".
const POINT_MOVE_THRESHOLDS: Record<string, number> = {
  btc_trend: 3, // BTC 30-day price change % (centered near 0)
  bch_momentum: 4, // BCH 30-day price change %
  bch_rel_strength: 4, // BCH minus BTC 30-day % (centered near 0)
  btc_dominance: 1.5, // BTC dominance % level — report point moves
};

// Contextual "watch next" chains and mechanism copy per indicator.
const WATCH_NEXT: Record<string, string[]> = {
  fima_repo: ['USD/JPY', '10Y Treasury', 'Fed Liquidity', 'BTC'],
  usdjpy: ['JGB 10Y', 'Japan UST holdings', 'DXY', 'BTC'],
  dxy: ['USD/JPY', 'US 10Y', 'Global Liquidity', 'BCH/BTC'],
  ust_10y: ['US 2Y', 'DXY', 'Fed Balance Sheet', 'BTC'],
  bch_btc: ['BTC Dominance', 'BTC Trend', 'BCH Volume', 'BCH Momentum'],
  bch_usd: ['BCH/BTC', 'BTC Trend', 'Total Mcap', 'Stablecoins'],
  reverse_repo: ['TGA', 'Fed Balance Sheet', 'SOFR', 'BTC'],
  tga: ['Reverse Repo', 'Fed Balance Sheet', 'US 2Y', 'BTC'],
  stablecoin_supply: ['Total Mcap', 'BTC Trend', 'BCH/USD', 'BCH Volume'],
};

function relevanceFor(
  indicatorId: string,
  signedMove: number,
): ChangeEvent['relevance'] {
  const def = INDICATORS_BY_ID[indicatorId];
  if (!def) return 'mixed';
  // supportive direction: value moving in the direction that raises sub-score
  const supportiveMove = def.direction === 1 ? signedMove > 0 : signedMove < 0;
  const magnitude = Math.abs(signedMove);
  const baseThreshold =
    POINT_MOVE_THRESHOLDS[indicatorId] ??
    THRESHOLDS[indicatorId] ??
    DEFAULT_THRESHOLD;
  if (magnitude < baseThreshold * 1.5) {
    return 'mixed';
  }
  return supportiveMove ? 'supportive' : 'negative';
}

function titleFor(
  indicatorId: string,
  pctSigned: number,
): { title: string; emoji: string } {
  const def = INDICATORS_BY_ID[indicatorId];
  const up = pctSigned > 0;
  const flag = def.group === 'japan' ? '🇯🇵'
    : def.group === 'fed' ? '🇺🇸'
    : def.group === 'bch' ? '🟢'
    : def.group === 'treasury' ? '🏛️'
    : '🌍';
  switch (indicatorId) {
    case 'usdjpy':
      return { title: up ? 'Yen stress increased' : 'Yen stabilized', emoji: '🇯🇵' };
    case 'dxy':
      return { title: up ? 'Dollar strengthened (tighter)' : 'Dollar liquidity improved', emoji: '🇺🇸' };
    case 'bch_btc':
      return { title: up ? 'BCH/BTC strengthened' : 'BCH/BTC weakened', emoji: '🟢' };
    case 'fima_repo':
      return { title: up ? 'FIMA repo usage increased' : 'FIMA repo usage fell', emoji: '🇺🇸' };
    case 'reverse_repo':
      return { title: up ? 'Reverse repo balances rose' : 'Reverse repo balances drained', emoji: '🇺🇸' };
    default:
      return { title: `${def.short} ${up ? 'rose' : 'fell'}`, emoji: flag };
  }
}

export function detectChanges(current: Observation[]): ChangeEvent[] {
  const events: ChangeEvent[] = [];

  for (const obs of current) {
    if (obs.value === null) continue;
    if (EXCLUDED_FROM_CHANGES.has(obs.indicatorId)) continue;
    const def = INDICATORS_BY_ID[obs.indicatorId];
    if (!def) continue;

    const pointMode = obs.indicatorId in POINT_MOVE_THRESHOLDS;

    // Find the largest meaningful move across windows (prefer shorter windows).
    // For point-mode indicators, "size" is the absolute change in points; for
    // everything else it's the ratio-based percent change.
    let best: {
      window: ChangeEvent['window'];
      from: number;
      pct: number; // ratio % (level indicators) or signed point change (point mode)
    } | null = null;

    for (const w of WINDOWS) {
      const past = getHistoricalValue(obs.indicatorId, w.ms);
      if (!past) continue;
      let magnitude: number;
      if (pointMode) {
        magnitude = obs.value - past.value; // signed points
      } else {
        if (past.value === 0) continue; // undefined ratio
        magnitude = ((obs.value - past.value) / Math.abs(past.value)) * 100;
        // Discard physically implausible moves — these are unit/data errors in
        // stored history, not real events (e.g. the fake TGA "-99.9%").
        const ceiling =
          SANITY_CEILING_PCT[obs.indicatorId] ?? DEFAULT_SANITY_CEILING;
        if (Math.abs(magnitude) > ceiling) continue;
      }
      if (!best || Math.abs(magnitude) > Math.abs(best.pct)) {
        best = { window: w.key, from: past.value, pct: magnitude };
      }
    }

    if (!best) continue;
    const threshold = pointMode
      ? POINT_MOVE_THRESHOLDS[obs.indicatorId]
      : THRESHOLDS[obs.indicatorId] ?? DEFAULT_THRESHOLD;
    if (Math.abs(best.pct) < threshold) continue;

    const { title, emoji } = titleFor(obs.indicatorId, best.pct);
    const relevance = relevanceFor(obs.indicatorId, best.pct);
    const category: ChangeEvent['category'] =
      def.group === 'bch' ? 'crypto' : 'liquidity';

    const sign = best.pct >= 0 ? '+' : '';
    const fromStr = best.from.toLocaleString(undefined, { maximumFractionDigits: 4 });
    const toStr = obs.value.toLocaleString(undefined, { maximumFractionDigits: 4 });
    // Point-mode: describe an absolute move in points (the value is already a
    // percentage), never a ratio "% of a %".
    const whatHappened = pointMode
      ? `${def.label} moved ${sign}${best.pct.toFixed(1)} ${best.pct === 1 ? 'point' : 'points'} over the last ${best.window} (from ${fromStr} to ${toStr} ${def.unit}).`
      : `${def.label} moved ${sign}${best.pct.toFixed(1)}% over the last ${best.window} (from ${fromStr} to ${toStr} ${def.unit}).`;

    events.push({
      id: `${obs.indicatorId}-${best.window}-${obs.timestamp}`,
      timestamp: obs.timestamp,
      indicatorId: obs.indicatorId,
      title,
      emoji,
      window: best.window,
      from: best.from,
      to: obs.value,
      pct: best.pct,
      whatHappened,
      whyItMatters: def.mechanism,
      relevance,
      watchNext: WATCH_NEXT[obs.indicatorId] ?? ['USD/JPY', 'DXY', 'BTC', 'BCH/BTC'],
      category,
    });
  }

  // Sort by magnitude, favoring shorter windows.
  const windowRank: Record<ChangeEvent['window'], number> = {
    '24h': 0, '7d': 1, '30d': 2, '90d': 3,
  };
  return events.sort((a, b) => {
    const w = windowRank[a.window] - windowRank[b.window];
    if (w !== 0) return w;
    return Math.abs(b.pct) - Math.abs(a.pct);
  });
}

export const RELEVANCE_META: Record<
  ChangeEvent['relevance'],
  { label: string; emoji: string; className: string }
> = {
  supportive: {
    label: 'Potentially supportive',
    emoji: '🟢',
    className: 'text-emerald-400',
  },
  mixed: {
    label: 'Mixed / uncertain',
    emoji: '🟡',
    className: 'text-yellow-300',
  },
  negative: {
    label: 'Potentially negative',
    emoji: '🔴',
    className: 'text-red-400',
  },
};
